import {Component, HostListener, Input, OnInit} from '@angular/core';
import {
    Address,
    BuildingType,
    DownloadableFile,
    LocationInfo,
    UpsertLocation
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {uuid} from "../../../../common/utils";
import {RefdataUtils} from "../../refdata-utils";
import {AppContext} from "../../../../app-context";
import {Handler} from "src/app/common/handler";
import {View} from "src/app/common/view";
import {Observable} from "rxjs";
import {EnergyCompassUtils} from "../../../charts/energy-compass-chart/energy-compass.utils";
import {Entity} from "../../../../handlers/entity";
import {DashboardContext} from "../../../dashboard/dashboard.context";
import {AppCommonUtils} from "../../../../common/app-common-utils";
import {LOCATION_IMAGE_CONTENT_TYPES, LOCATION_IMAGE_MAX_BYTES, readFileAsBase64} from "../location-image.utils";
import {TranslateDirective} from "../../../../common/utils/translate.directive";
import {openConfirmationModalWithCallback} from "../../../../app-utils";
import {
    ModalConfirmAutofocus,
    ModalConfirmAutofocusData
} from "../../../../common/modal-confirm/modal-confirm.component";
import {hasFullElectricConflictingConnections} from './full-electric-location.utils';
import {
    explicitlyClosedOpeningDays,
    locationOpeningHourOverrides,
    OpeningHoursRow,
    resolveOpeningHoursRows
} from './opening-hours.utils';

@Component({
  selector: 'app-location-details',
  templateUrl: './location-details.component.html',
  styleUrls: ['./location-details.component.scss']
})
@Handler()
export class LocationDetailsComponent extends View implements OnInit {
  energyCompassUtils = EnergyCompassUtils;
  appContext = AppContext;
  refDataUtils = RefdataUtils;
  @Input() data: LocationComponentData;
  isNewLocation: boolean;
  showOrganisationField: boolean;
  info: LocationInfo;
  initialAllElectric: boolean;
  buildingType: BuildingType;
  buildingTypes: Observable<BuildingType[]>;
  daysOfWeek: string[] = DashboardContext.weekdaysSorted;
  openingHoursRows: OpeningHoursRow[] = [];
  locationImage: DownloadableFile = null;
  hasLocationImage = false;
  addressStep: 'lookup' | 'details';
  addressLookup: AddressLookupInput = {zipCode: '', number: '', addition: ''};
  addressLookupState: 'idle' | 'searching' | 'found' | 'not-found' | 'error' = 'idle';
  resolvedAddress: ResolvedLocationAddressResult;
  addressResolvedAutomatically = false;
  automaticEnergyLabel: string;
  energyLabelDetailsOpen = false;
  readonly energyLabelDetailsId = 'energy-label-details-' + uuid();
  private energyLabelTrigger: HTMLButtonElement;
  private energyLabelPopup: HTMLElement;
  private addressLookupRevision = 0;
  private openingHoursChanged = false;

  ngOnInit(): void {
    this.isNewLocation = !this.data.entity.location?.locationId;
    this.showOrganisationField = this.isNewLocation && !this.data.entity.organisation;
    this.info = this.data.entity.location?.info ? {
          ...this.data.entity.location.info,
          allElectric: this.data.entity.location.info.allElectric ?? false,
          openingHours: [...(this.data.entity.location.info.openingHours ?? [])],
          closedOpeningDays: [...((this.data.entity.location.info as any).closedOpeningDays ?? [])]
        } : LocationDetailsComponent.newLocation();
    this.addressStep = this.isNewLocation ? 'lookup' : 'details';
    this.initialAllElectric = !!this.info.allElectric;
    if (this.info.buildingType) {
      this.sendQuery("getBuildingType", this.info.buildingType).subscribe(b => this.onBuildingTypeChange(b));
    } else {
      this.refreshOpeningHoursRows();
    }
    this.buildingTypes = this.sendQuery("findBuildingTypes");
    this.loadLocationImage();
  }

  searchOrganisations = (term: string) => this.sendQuery("searchOrganisations", term);

  submitForm = () => {
    if (this.addressStep === 'lookup') {
      this.searchAddress();
      return;
    }
    this.updateInfo();
  };

  searchAddress = () => {
    if (!this.canSearchAddress() || this.addressLookupState === 'searching') {
      return;
    }
    this.addressLookup.zipCode = this.addressLookup.zipCode.replace(/\s/g, '').toUpperCase();
    const lookupRevision = ++this.addressLookupRevision;
    this.addressLookupState = 'searching';
    this.resolvedAddress = null;
    this.sendQuery("com.flowmaps.api.organisation.ResolveLocationAddress", {
      zipCode: this.addressLookup.zipCode,
      number: this.addressLookup.number.trim(),
      addition: this.addressLookup.addition?.trim() || null
    }, {caching: false, showSpinner: false, hideError: true}).subscribe({
      next: (result: ResolvedLocationAddressResult) => {
        if (lookupRevision !== this.addressLookupRevision) {
          return;
        }
        this.resolvedAddress = result;
        this.addressLookupState = result ? 'found' : 'not-found';
        if (result) {
          this.lookupEnergyLabel(result, lookupRevision);
        }
      },
      error: () => {
        if (lookupRevision === this.addressLookupRevision) {
          this.addressLookupState = 'error';
        }
      }
    });
  };

  addressLookupChanged = (field: keyof AddressLookupInput, value: string) => {
    this.addressLookup[field] = field === 'zipCode' ? value.toUpperCase() : value;
    this.addressLookupRevision++;
    this.addressLookupState = 'idle';
    this.resolvedAddress = null;
  };

  canSearchAddress(): boolean {
    return /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/.test(this.addressLookup.zipCode?.trim() ?? '')
      && /^[0-9]+$/.test(this.addressLookup.number?.trim() ?? '');
  }

  useResolvedAddress = () => {
    if (!this.resolvedAddress) {
      return;
    }
    this.info.address = {
      ...this.resolvedAddress.address,
      geoLocation: {...this.resolvedAddress.address.geoLocation}
    };
    this.info.cadastreId = this.resolvedAddress.cadastreId;
    this.automaticEnergyLabel = this.resolvedAddress.energyLabel;
    if (this.automaticEnergyLabel) {
      this.info.energyLabel = this.automaticEnergyLabel;
      this.info.energyLabelAutomatic = true;
    }
    this.addressResolvedAutomatically = true;
    this.addressStep = 'details';
  };

  useManualAddress = () => {
    this.addressLookupRevision++;
    this.info.address = <Address>{country: 'NL', geoLocation: {}};
    this.info.cadastreId = null;
    if (this.automaticEnergyLabel && this.info.energyLabel === this.automaticEnergyLabel) {
      this.info.energyLabel = null;
    }
    this.automaticEnergyLabel = null;
    this.addressResolvedAutomatically = false;
    this.addressStep = 'details';
  };

  reopenAddressLookup = () => {
    this.addressLookupRevision++;
    const address = this.info.address;
    this.addressLookup = {
      zipCode: address?.zipCode ?? '',
      number: address?.number ?? '',
      addition: address?.addition ?? ''
    };
    this.addressLookupState = 'idle';
    this.resolvedAddress = null;
    this.addressStep = 'lookup';
  };

  resolvedAddressLine(): string {
    return this.resolvedAddress ? RefdataUtils.addressFormatter(this.resolvedAddress.address) : '';
  }

  private lookupEnergyLabel(result: ResolvedLocationAddressResult, lookupRevision: number) {
    this.sendQuery("com.flowmaps.api.organisation.LookupLocationEnergyLabel", {
      address: result.address,
      cadastreId: result.cadastreId
    }, {caching: false, showSpinner: false, hideError: true}).subscribe({
      next: (energyLabelResult: LocationEnergyLabelResult) => {
        if (lookupRevision !== this.addressLookupRevision || this.resolvedAddress !== result) {
          return;
        }
        result.energyLabel = energyLabelResult.energyLabel;
        result.epOnlineStatus = energyLabelResult.status;
        if (energyLabelResult.energyLabel) {
          this.automaticEnergyLabel = energyLabelResult.energyLabel;
          if (this.addressResolvedAutomatically) {
            this.info.energyLabel = energyLabelResult.energyLabel;
            this.info.energyLabelAutomatic = true;
          }
        }
      },
      error: () => {
        if (lookupRevision === this.addressLookupRevision && this.resolvedAddress === result) {
          result.epOnlineStatus = 'NETWORK_ERROR';
        }
      }
    });
  }

  buildingTypeFormatter = (buildingType: BuildingType) => buildingType.info.name;

  onBuildingTypeChange = (buildingType: BuildingType) => {
    this.buildingType = buildingType;
    this.refreshOpeningHoursRows(true);
  };

  onOpeningTimeChange(day: OpeningHoursRow, field: 'start' | 'end', value: string): void {
    day[field] = value || null;
    day.closed = false;
    day.inherited = false;
    this.openingHoursChanged = true;
  }

  onClosedChange(day: OpeningHoursRow, closed: boolean): void {
    this.openingHoursChanged = true;
    if (closed) {
      day.start = null;
      day.end = null;
      day.closed = true;
      day.inherited = false;
      return;
    }
    day.closed = false;
    day.inherited = false;
    const defaultHours = this.buildingType?.info?.defaultOpeningHours?.find(hours => hours.dayOfWeek === day.dayOfWeek);
    day.start = defaultHours?.start ?? null;
    day.end = defaultHours?.end ?? null;
  }

  updateInfo = () => {
    const discardedOpeningHours = this.hasInvalidOpeningHours();
    this.info.buildingType = this.buildingType ? this.buildingType.buildingTypeId : null;
    this.info.openingHours = locationOpeningHourOverrides(this.openingHoursRows);
    (this.info as any).closedOpeningDays = explicitlyClosedOpeningDays(this.openingHoursRows);
    const locationId = this.isNewLocation ? uuid() : this.data.entity.location.locationId;
    this.sendCommand("com.flowmaps.api.organisation.UpsertLocation", <UpsertLocation> {
      organisationId: this.data.entity.organisation.organisationId,
      locationId,
      info: this.info,
      refdata: this.data.entity.location?.refdata
    }, () => this.saveLocationImageChange(locationId, discardedOpeningHours));
  };

  selectLocationImage = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) {
      return;
    }
    if (!LOCATION_IMAGE_CONTENT_TYPES.includes(file.type)) {
      this.locationImage = null;
      input.setCustomValidity(TranslateDirective.getTranslation("Only JPG, PNG and WebP images are supported.", true));
      return;
    }
    if (file.size > LOCATION_IMAGE_MAX_BYTES) {
      this.locationImage = null;
      input.setCustomValidity(TranslateDirective.getTranslation("The image must be 2 MB or smaller.", true));
      return;
    }
    input.setCustomValidity("");
    readFileAsBase64(file).subscribe({
      next: data => this.locationImage = {
        base64Data: data,
        fileName: file.name,
        mediaType: file.type
      },
      error: error => AppCommonUtils.registerError(error)
    });
  };

  removeLocationImage = () => {
    this.locationImage = null;
  }

  private saveLocationImageChange(locationId: string, discardedOpeningHours: boolean) {
    if (this.locationImage?.base64Data) {
      this.uploadLocationImage(locationId, discardedOpeningHours);
      return;
    }
    if (this.hasLocationImage && !this.locationImage) {
      this.sendCommand("com.flowmaps.api.organisation.RemoveLocationImage", {
        organisationId: this.data.entity.organisation.organisationId,
        locationId
      }, () => this.registerLocationSaved(discardedOpeningHours));
      return;
    }
    this.registerLocationSaved(discardedOpeningHours);
  }

  private uploadLocationImage(locationId: string, discardedOpeningHours: boolean) {
    this.sendCommand("com.flowmaps.api.organisation.UploadLocationImage", {
      organisationId: this.data.entity.organisation.organisationId,
      locationId,
      contentType: this.locationImage.mediaType,
      fileName: this.locationImage.fileName,
      data: this.locationImage.base64Data
    }, () => this.registerLocationSaved(discardedOpeningHours));
  }

  private registerLocationSaved(discardedOpeningHours: boolean): void {
    this.openingHoursChanged = false;
    AppCommonUtils.registerSuccess("Location was updated successfully");
    if (discardedOpeningHours) {
      AppCommonUtils.addAlert({
        level: 'warning',
        content: 'Some opening hours were not saved. Incomplete days have returned to the building-type defaults.',
        msShowTime: 6000
      });
    }
  }

  requestClose = (): void => {
    if (!this.openingHoursChanged) {
      this.sendCommandAndForget("closeModal");
      return;
    }
    openConfirmationModalWithCallback(discard => {
      if (discard !== true) {
        return;
      }
      this.openingHoursChanged = false;
      this.sendCommandAndForget("closeModal");
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: 'warning',
      title: 'Discard opening-hour changes?',
      message: 'The adjusted opening hours have not been saved. If you close this window, those days will return to the building-type defaults.',
      confirmText: 'Discard changes',
      cancelText: 'Continue editing'
    }, 'static');
  };

  isInvalidRange(day: OpeningHoursRow): boolean {
    if (day.closed) {
      return false;
    }
    if (!day.inherited && (!day.start || !day.end)) {
      return true;
    }
    return (!!day.start !== !!day.end) || (!!day.start && day.start === day.end);
  }

  hasInvalidOpeningHours(): boolean {
    return this.openingHoursRows.some(day => this.isInvalidRange(day));
  }

  isEpOnlineLinked(): boolean {
    const refdata = this.data?.entity?.location?.refdata;
    const enteredId = this.info?.cadastreId?.trim();
    const verifiedId = refdata?.cadastreId?.trim();
    const refdataLinked = !!enteredId && enteredId === verifiedId
      && !!refdata?.energyLabel && !!refdata.energyLabelLastCheckedAt;
    const stagedLookupLinked = !!enteredId
      && enteredId === this.resolvedAddress?.cadastreId?.trim()
      && this.resolvedAddress?.epOnlineStatus === 'FOUND'
      && !!this.resolvedAddress.energyLabel;
    return refdataLinked || stagedLookupLinked;
  }

  getEnergyLabelDetails() {
    const refdata = this.data?.entity?.location?.refdata;
    if (this.info.energyLabelAutomatic === false || !this.isBagIdComplete() || this.info.cadastreId.trim() !== refdata?.cadastreId?.trim()
        || !refdata?.energyLabelLastCheckedAt || !refdata.energyLabel?.trim()
        || this.info.energyLabel?.trim() !== refdata.energyLabel.trim()) {
      return null;
    }
    return refdata;
  }

  energyLabelChanged(): void {
    this.closeEnergyLabelDetails();
    const refdata = this.data?.entity?.location?.refdata;
    this.info.energyLabelAutomatic = !!this.info.energyLabel && !!refdata?.energyLabelLastCheckedAt
      && this.info.cadastreId?.trim() === refdata.cadastreId?.trim()
      && this.info.energyLabel.trim() === refdata.energyLabel?.trim();
  }

  toggleEnergyLabelDetails(event: Event, trigger: HTMLButtonElement, popup: HTMLElement): void {
    event.preventDefault();
    if (popup.matches(':popover-open')) {
      this.closeEnergyLabelDetails();
      return;
    }
    this.energyLabelTrigger = trigger;
    this.energyLabelPopup = popup;
    popup.showPopover();
    this.energyLabelDetailsOpen = true;
    const anchor = trigger.getBoundingClientRect();
    const bounds = popup.getBoundingClientRect();
    popup.style.left = Math.max(12, Math.min(anchor.right - bounds.width, window.innerWidth - bounds.width - 12)) + 'px';
    const below = anchor.bottom + 8;
    popup.style.top = (below + bounds.height <= window.innerHeight - 12
      ? below : Math.max(12, anchor.top - bounds.height - 8)) + 'px';
  }

  closeEnergyLabelDetails(restoreFocus = false): void {
    this.energyLabelPopup?.hidePopover();
    this.energyLabelDetailsOpen = false;
    if (restoreFocus) {
      this.energyLabelTrigger?.focus();
    }
  }

  @HostListener('keydown.escape', ['$event'])
  closeEnergyLabelDetailsWithEscape(event: KeyboardEvent): void {
    if (this.energyLabelDetailsOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closeEnergyLabelDetails(true);
    }
  }

  @HostListener('window:resize')
  closeEnergyLabelDetailsOnResize(): void {
    this.closeEnergyLabelDetails();
  }

  energyLabelCloseTitle(): string {
    return TranslateDirective.getTranslation('Close energy label details', true);
  }

  energyLabelDetailsTitle(): string {
    return TranslateDirective.getTranslation('EP-Online energy label details', true);
  }

  formatEnergyLabelDate(value: string, includeTime = false): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    const locale = AppCommonUtils.getPreferredLanguage() === 'nl' ? 'nl-NL' : 'en-GB';
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Amsterdam',
      ...(includeTime ? {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'} as const : {})
    }).format(date);
  }

  isBagIdComplete(): boolean {
    return /^\d{16}$/.test(this.info?.cadastreId?.trim() ?? '');
  }

  allElectricStatusLabel(): string {
    const status = TranslateDirective.getTranslation(this.info?.allElectric ? 'yes' : 'no', true);
    if (!this.hasUnsavedAllElectricChange()) {
      return status;
    }
    const reminder = TranslateDirective.getTranslation('don\'t forget to save', true);
    return `${status} (${reminder})`;
  }

  hasUnsavedAllElectricChange(): boolean {
    return !!this.info?.allElectric !== this.initialAllElectric;
  }

  onAllElectricChange(enabled: boolean): void {
    if (!enabled) {
      this.info.allElectric = false;
      return;
    }
    if (!hasFullElectricConflictingConnections(this.data?.entity?.location)) {
      this.info.allElectric = true;
      return;
    }

    openConfirmationModalWithCallback(confirmed => {
      this.info.allElectric = confirmed === true;
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: 'warning',
      title: 'Enable Full Electric',
      message: 'Please note: you have indicated that this building is Full Electric, but gas and/or heat connections are linked to this location. By enabling Full Electric, all WEii scores will from now on be calculated without gas, heat or cooling data.',
      confirmText: 'Enable Full Electric',
      cancelText: 'Cancel'
    }, 'static');
  }

  private loadLocationImage() {
    if (this.isNewLocation) {
      return;
    }
    this.sendQuery("com.flowmaps.api.organisation.GetLocationImage", {
      locationId: this.data.entity.location.locationId
    }, {caching: false, hideError: true}).subscribe((file: DownloadableFile) => {
      this.locationImage = file ? {
        fileName: file.fileName,
        mediaType: file.mediaType
      } : null;
      this.hasLocationImage = !!file;
    });
  }

  static newLocation = (): LocationInfo => ({
    allElectric: false,
    address: {
      country: 'NL',
      geoLocation: {}
    },
    openingHours: [],
    closedOpeningDays: []
  });

  private refreshOpeningHoursRows(preserveEdits = false): void {
    const defaults = this.buildingType?.info?.defaultOpeningHours ?? [];
    const currentOverrides = preserveEdits && this.openingHoursRows.length
      ? locationOpeningHourOverrides(this.openingHoursRows) : (this.info.openingHours ?? []);
    const currentClosures = preserveEdits && this.openingHoursRows.length
      ? explicitlyClosedOpeningDays(this.openingHoursRows) : ((this.info as any).closedOpeningDays ?? []);
    this.openingHoursRows = resolveOpeningHoursRows(this.daysOfWeek, currentOverrides, currentClosures, defaults);
  }
}

export interface LocationComponentData {
  entity: Entity;
}

interface AddressLookupInput {
  zipCode: string;
  number: string;
  addition: string;
}

interface ResolvedLocationAddressResult {
  address: Address;
  cadastreId: string;
  energyLabel: string;
  epOnlineStatus: 'PENDING' | 'FOUND' | 'NOT_FOUND' | 'UNAVAILABLE' | 'CONFIGURATION_ERROR'
    | 'AUTHENTICATION_FAILED' | 'INVALID_RESPONSE' | 'NETWORK_ERROR';
}

interface LocationEnergyLabelResult {
  energyLabel: string;
  status: ResolvedLocationAddressResult['epOnlineStatus'];
}
